import type { BaseColumnData } from '@/utils/tables/column-data';
import { translateKey } from '@/utils/translation-utils';
import { models, utils } from 'cesg-shared';
import { type ComposerTranslation } from 'vue-i18n';

export const supplierInvoiceAmountKey = 'totalAmount';

export function getSupplierInvoiceColumns(t: ComposerTranslation) {
  const supplierInvoiceColumns: BaseColumnData[] = [
    {
      key: 'supplierInvoiceNumber',
      label: t('invoices.table.supplierInvoiceNumber'),
      sortable: true,
    },
    {
      key: 'accountType',
      label: t('invoices.table.accountType'),
      sortable: true,
    },
    {
      key: 'invoiceNumber',
      label: t('invoices.table.invoiceNumber'),
      sortable: true,
    },
    {
      key: 'status',
      label: t('invoices.table.status'),
      format: translateKey,
      sortable: true,
    },
    { key: 'supplier', label: t('invoices.table.supplier'), sortable: true },
    {
      key: supplierInvoiceAmountKey,
      label: t('invoices.table.totalAmount'),
      align: 'has-text-right',
      format: utils.formatNumber,
      sortable: true,
    },
    {
      key: 'invoiceDate',
      label: t('invoices.table.invoiceDate'),
      align: 'has-text-right',
      sortable: true,
    },
    {
      key: 'dueDate',
      label: t('invoices.table.dueDate'),
      align: 'has-text-right',
      sortable: true,
    },
    {
      key: 'approver',
      label: t('invoices.table.approver'),
      align: 'has-text-right',
      sortable: true,
    },
  ];
  return supplierInvoiceColumns;
}

// Status groups are shown in this order; any other status falls after these.
const statusSortOrder = [
  models.SupplierInvoiceStatusType.Disputed,
  models.SupplierInvoiceStatusType.Parked,
  models.SupplierInvoiceStatusType.UnderCertificate,
];

function getStatusRank(status: models.SupplierInvoicesResponse['status']) {
  const index = statusSortOrder.indexOf(
    status as models.SupplierInvoiceStatusType,
  );
  return index === -1 ? statusSortOrder.length : index;
}

// Highest serial number first.
function compareSerialNumberDesc(
  a: models.SupplierInvoicesResponse,
  b: models.SupplierInvoicesResponse,
) {
  const serialA = a.supplierInvoiceNumber ?? '';
  const serialB = b.supplierInvoiceNumber ?? '';
  return serialB.localeCompare(serialA, undefined, { numeric: true });
}

export function sortSupplierInvoices(
  data: models.SupplierInvoicesResponse[],
): models.SupplierInvoicesResponse[] {
  return [...data].sort((a, b) => {
    const rankA = getStatusRank(a.status);
    const rankB = getStatusRank(b.status);

    if (rankA !== rankB) {
      return rankA - rankB;
    }

    // Statuses outside the priority groups: serial number only, highest first.
    if (rankA === statusSortOrder.length) {
      return compareSerialNumberDesc(a, b);
    }

    // Priority groups: soonest dueDate first, then highest serial number as backup.
    const dueA = a.dueDate ? new Date(a.dueDate).getTime() : null;
    const dueB = b.dueDate ? new Date(b.dueDate).getTime() : null;

    if (dueA !== null && dueB !== null) {
      if (dueA !== dueB) {
        return dueA - dueB;
      }
    } else if (dueA !== null) {
      return -1;
    } else if (dueB !== null) {
      return 1;
    }

    return compareSerialNumberDesc(a, b);
  });
}
